import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { selectToken } from '../features/auth/authSlice';
import { ImageObject } from '../utils/getImageUrl';

const baseUrl =
  process.env.REACT_APP_BASE_URL || 'https://service2.funifier.com/v3';

/**
 * Hook para carregar imagens autenticadas
 * Faz fetch com header Authorization e retorna blob URL
 */
export function useAuthenticatedImage(
  item: File | string | ImageObject | null | undefined,
): string {
  const token = useSelector(selectToken);
  const [imageUrl, setImageUrl] = useState<string>('');

  useEffect(() => {
    if (!item) {
      setImageUrl('');
      return;
    }

    // Se for File, cria ObjectURL
    if (item instanceof File) {
      const url = URL.createObjectURL(item);
      setImageUrl(url);
      return () => URL.revokeObjectURL(url);
    }

    // Se for string (URL legada ou blob), usa direto
    if (typeof item === 'string') {
      setImageUrl(item);
      return;
    }

    // Se for objeto com url (formato legado)
    if ('url' in item && item.url) {
      setImageUrl(item.url);
      return;
    }

    // Se for objeto com _id, faz fetch autenticado
    if ('_id' in item && item._id) {
      const controller = new AbortController();
      const imageId = item._id;

      (async () => {
        try {
          const response = await fetch(`${baseUrl}/upload/${imageId}/file`, {
            headers: {
              Authorization: `Bearer ${token?.access_token}`,
            },
            cache: 'no-store',
            signal: controller.signal,
          });

          if (response.ok) {
            const blob = await response.blob();
            const url = URL.createObjectURL(blob);
            setImageUrl(url);
          }
        } catch (error) {
          if ((error as Error).name !== 'AbortError') {
            console.error('Erro ao carregar imagem:', error);
          }
        }
      })();

      return () => {
        controller.abort();
      };
    }
  }, [item, token?.access_token]);

  return imageUrl;
}
